import { api, PagedResultOfUser, User, UserCentre, UserRegion, UserWithApp } from 'api';
import { rtkQueryApi } from 'store/rtkQueryApi';
import { TableState } from 'types';
import { getCentresIds, getPaginationParameters, getQueryStringParameters } from 'utils';
import { UserFilters } from './usersTypes';

type UsersListParams = TableState<User, UserFilters>;

type SaveUserRequest = Pick<User, 'firstName' | 'lastName' | 'emailAddress' | 'roleId'> & {
  id?: number;
  regionIds: number[];
  centreIds: number[];
};

const mapQueryParams = (params: UsersListParams) => {
  const paginationParams = getPaginationParameters(params);

  if (paginationParams.sorting?.sortField) {
    paginationParams.sorting.sortField = `user.${paginationParams.sorting.sortField}`;
  }

  return {
    ...paginationParams,
    filter: {
      searchText: params.filters.searchQuery ?? undefined,
      centreIds: getCentresIds(params.filters.centresIds),
      regionIds: params.filters.regionsIds,
      roleId: params.filters.roleId ?? undefined,
    },
  };
};

export const usersApi = rtkQueryApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<PagedResultOfUser, UsersListParams>({
      query: (params) => {
        const input = getQueryStringParameters(mapQueryParams(params));
        return api.user.getUsers(input);
      },
      providesTags: ['Users'],
    }),

    getUserDetails: builder.query<User, number>({
      query: (userId) => api.user.getUserDetails({ userId }),
      providesTags: ['Users'],
    }),

    saveUserDetails: builder.mutation<User, SaveUserRequest>({
      query: ({ regionIds, centreIds, ...data }) =>
        api.user.saveUserDetails({
          ...data,
          //TODO: Update API to accept UserFormData directly and remove the need for this mapping
          regions: regionIds.map((regionId) => ({ regionId }) as UserRegion),
          centres: centreIds.map((centreId) => ({ centreId }) as UserCentre),
        } as User),
      invalidatesTags: ['Users'],
    }),

    revokeAccess: builder.mutation<void, number>({
      query: (userId) => api.user.revokeAccess({ userId }),
      invalidatesTags: ['Users'],
    }),

    getUserByEmail: builder.query<UserWithApp, string>({
      query: (email) => api.user.getUserByEmail({ email }),
    }),

    getUsersAssignedOrRevoked: builder.query<string[], string>({
      query: (searchText) => api.user.getUsersAssignedOrRevoked({ searchText }),
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserDetailsQuery,
  useSaveUserDetailsMutation,
  useRevokeAccessMutation,
  useLazyGetUserByEmailQuery,
  useLazyGetUsersAssignedOrRevokedQuery,
} = usersApi;
